import { Fragment, useContext, useState, useEffect, useCallback } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'react-bootstrap';
import { Divider, Flex } from 'theme-ui';
import { Button } from '@velove/design-system';
import { DeliveryStatusType } from '../../contexts/contextTypes';

// @ts-ignore
import IBoxen from '../../assets/images/IBoxen.svg';
import dayjs from 'dayjs';

interface LockerDeliveryContainerProps {
  isCustomerService: boolean;
}

const LockerDeliveryContainer = ({ isCustomerService }: LockerDeliveryContainerProps) => {
  const { t } = useTranslation();
  const { trackingDetails } = useContext(AppContext);

  const disableOpenLocker = isCustomerService || trackingDetails?.task?.status !== DeliveryStatusType.CONSUMER_COLLECTION;
  const lockerAddress = trackingDetails?.packageLocker
    ? `${trackingDetails.packageLocker.street}, ${trackingDetails.packageLocker.postalCode} ${trackingDetails.packageLocker.city}`
    : '';

  const lockerExpiresAt = trackingDetails?.task?.lockerExpiresAt;
  const isReadyForPickup = trackingDetails?.task?.status === DeliveryStatusType.CONSUMER_COLLECTION;

  const calcTimeRemaining = useCallback(() => {
    if (!lockerExpiresAt) return null;
    const now = dayjs();
    const expiry = dayjs(lockerExpiresAt);
    const diffMs = expiry.diff(now);
    if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds, expired: false };
  }, [lockerExpiresAt]);

  const [timeRemaining, setTimeRemaining] = useState(calcTimeRemaining);

  useEffect(() => {
    if (!isReadyForPickup || !lockerExpiresAt) return;
    const interval = setInterval(() => {
      setTimeRemaining(calcTimeRemaining());
    }, 1000);
    return () => clearInterval(interval);
  }, [isReadyForPickup, lockerExpiresAt, calcTimeRemaining]);

  const pad = (n: number) => String(n).padStart(2, '0');

  const formatCountdown = () => {
    if (!timeRemaining || timeRemaining.expired) return t('lockerExpired') || 'Expired';
    const { days, hours, minutes, seconds } = timeRemaining;
    return days > 0 ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const handleOpenIBoxen = () => {
    const IBOXEN_DEEP_LINK = 'iboxenconsumer://'; //'com.iboxenconsumerapp://'; //'iboxen://'; //com.iboxen.consumerapp://
    const IBOXEN_IOS_APP_STORE = 'https://apps.apple.com/se/app/iboxen/id1553931951';
    const IBOXEN_ANDROID_PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.iboxenconsumerapp';

    // Attempt to open the app
    window.location.href = IBOXEN_DEEP_LINK;

    // Fallback logic: if the app doesn't open within a seconds, go to the store
    setTimeout(() => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /ipad|iphone|ipod/.test(userAgent);

      if (isIOS) {
        window.location.href = IBOXEN_IOS_APP_STORE;
      } else {
        window.location.href = IBOXEN_ANDROID_PLAY_STORE;
      }
    }, 1000);
  };

  if (trackingDetails?.task?.taskType !== 'LOCKER_DELIVERY') {
    return null;
  }

  return (
    <Fragment>
      <Container style={{ marginTop: 20, marginBottom: 20 }}>
        {isReadyForPickup ? (
          <div>
            <Flex sx={{ mb: 1 }}>
              {`${t('lockerPickupUntil') || 'You can pick it up until'}: ${dayjs(lockerExpiresAt || new Date()).format('YYYY-MM-DD, HH:mm')}`}
            </Flex>
            <Flex sx={{ mb: 1, fontWeight: 'bold', fontSize: 18 }}>{`${t('timeRemaining') || 'Time Remaining'}: ${formatCountdown()}`}</Flex>
            <Flex sx={{ mb: 2 }}>{t('lockerPickupInstruction') || 'Please use the button below to open the app and access your locker.'}</Flex>
          </div>
        ) : (
          <Flex sx={{ mb: 2 }}>{t('lockerDeliveryOnTheWayToLocker').replace(/(##provider##)/, trackingDetails?.packageLocker?.provider || 'iBoxen')}</Flex>
        )}
        <Flex sx={{ mb: 20 }}>
          {`${t('lockerAddress')}: `}
          <a
            href={
              trackingDetails?.packageLocker?.latitude && trackingDetails?.packageLocker?.longitude
                ? `https://www.google.com/maps/search/?api=1&query=${trackingDetails.packageLocker.latitude},${trackingDetails.packageLocker.longitude}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lockerAddress)}`
            }
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 4, color: '#0d0d0eff', textDecoration: 'underline' }}
          >
            {lockerAddress}
          </a>
        </Flex>
        <Row>
          <Flex className="iboxen-container">
            <Button
              className="iboxen-button"
              pt={'5px'}
              pb={'5px'}
              onClick={handleOpenIBoxen}
              disabled={disableOpenLocker}
              sx={{
                opacity: !disableOpenLocker ? 1 : 0.3
              }}
            >
              <img src={IBoxen} width={30} height={30} alt="IBoxen_logo" style={{ marginRight: 5 }} />
              {t('openIBoxenApp') || 'Open iBoxen App'}
            </Button>
          </Flex>
        </Row>
      </Container>
      <Divider style={{ marginTop: 20, marginBottom: 0 }} />
    </Fragment>
  );
};

export default LockerDeliveryContainer;
